import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CellClickEvent, CellCloseEvent } from '@progress/kendo-angular-grid';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { DataverseService } from 'src/app/modules/app-dataverse/services/dataverse.service';
import { DataverseApiService } from 'src/app/modules/app-dataverse/services/dataverse-api.service';
import { TranslationModel } from 'src/app/modules/app-settings/models/translation-model';
import { SupportedFieldDropdown } from 'src/app/modules/app-settings/models/supported-field-dropdown';

/**
 * Reflist translation dialog
 */
@Component({
  selector: 'ref-list-translate-dialog',
  templateUrl: './ref-list-translate-dialog.component.html',
  styleUrls: ['./ref-list-translate-dialog.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class RefListTranslateDialogComponent implements OnInit, OnDestroy {
  /**
   * Constructor
   * @param dataverseApiService
   * @param ref
   * @param formBuilder
   * @param dataverseService
   */
  constructor(private dataverseApiService: DataverseApiService, private ref: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder, private dataverseService: DataverseService) { }

  /**
 * Reference list name
 */
  @Input() referenceListid: string;
  /**
* Field name
*/
  @Input() fieldName: string;
  /**
 * Field id
 */
  @Input() fieldId: number;

  /**
 * Event emitter for closing dialog
 */
  @Output() close: EventEmitter<boolean> = new EventEmitter();

  /**
 * Displayed data
 */
  translationModel: TranslationModel[] = [];

  /**
 * Flag if loader should be shown
 */
  displayLoader = true;

  /**
 * Error message from api
 */
  errorMsg: string

  /**
   * The (list of) subscription(s)
   */
  subscriptions: Subscription = new Subscription();

  /**
*On Destroy
*/
  ngOnDestroy(): void {
    if (this.subscriptions.unsubscribe) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
 * ngOnInit
 */
  ngOnInit(): void {
    forkJoin({
      refList: this.dataverseApiService.getRefList(this.referenceListid),
      supportedField: this.dataverseService.getSupportedFieldDropdown(this.fieldId, this.referenceListid),
    })
      .subscribe(({ refList, supportedField }) => {
        refList.forEach(value => {
          const refListValue = new TranslationModel();
          refListValue.id = value.oid;
          if (supportedField.find(f => f.refOid == value.oid)) {
            refListValue.translation = supportedField.find(f => f.refOid == value.oid).description;
          }
          refListValue.description = value.description;
          this.translationModel.push(refListValue);
        })
        this.displayLoader = false;
        this.ref.markForCheck();
      });
  }

  /**
* Checks if Save button should be disabled
*/
  isSaveBtnDisabled() {
    return this.translationModel.find(t => !t.translation && t.id != 0);
  }

  /**
* Save data
*/
  saveData() {
    const updatedData: SupportedFieldDropdown[] = [];

    this.translationModel.forEach((value) => {
      const translation = new SupportedFieldDropdown();
      translation.fieldId = this.fieldId;
      translation.refOid = value.id;
      translation.refTable = this.referenceListid;
      translation.description = value.translation;
      updatedData.push(translation);
    })

    this.dataverseService.upsertSupportedFieldDropdown(updatedData).subscribe(result => {
      this.displayLoader = false;

      if (result.success) {
        this.close.emit();
      } else {
        this.errorMsg = "Something went wrong. If this issue persists, please contact support"
      }
    })

  }

  /**
* Close dialog
*/
  closeDialog() {
    this.close.emit();
  }

  /**
* Edit cell on click
*/
  public cellClickHandler(args: CellClickEvent): void {
    if (!args.isEdited) {
      args.sender.editCell(
        args.rowIndex,
        args.columnIndex,
        this.createFormGroup(args.dataItem)
      );
    }

    this.ref.markForCheck();
  }

  /**
* Exits cell edit mode
*/
  public cellCloseHandler(args: CellCloseEvent): void {
    const { formGroup, dataItem } = args;
    Object.assign(dataItem, formGroup.value);

  }

  /**
* Form group
*/
  public createFormGroup(dataItem: TranslationModel): UntypedFormGroup {
    return this.formBuilder.group({
      id: dataItem.id,
      translation: [dataItem.translation, Validators.required],
      description: dataItem.description,
    });
  }
}
